import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout, useMenu } from '@refinedev/core';
import { Layout, Menu, Button, Tooltip, Tag, Dropdown } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  SettingOutlined,
  LogoutOutlined,
  SwapOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useService, services, ServiceType, isBusOnlyMode, isLockedMode, isTaxiOnlyMode, isBusCompanyUser } from '../../contexts/ServiceContext';

const { Sider } = Layout;

interface CustomSiderProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const serviceIcons: Record<ServiceType, React.ReactNode> = {
  taxi: <img src="/ic_intercity_icon.png" alt="Shaharlararo Taxi" style={{ width: 20, height: 20, objectFit: 'contain' }} />,
  bus: <img src="/bus_icon.png" alt="Avtobuslar" style={{ width: 20, height: 20, objectFit: 'contain' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />,
  cargo: null,
};

const serviceColors: Record<ServiceType, string> = {
  taxi: '#3498DB',
  bus: '#9B59B6',
  cargo: '#E67E22',
};

export const CustomSider = ({ collapsed, setCollapsed }: CustomSiderProps) => {
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const { menuItems } = useMenu();
  const { selectedService, setSelectedService, currentServiceInfo } = useService();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const busOnlyMode = isBusOnlyMode();
  const taxiOnlyMode = isTaxiOnlyMode();
  const lockedMode = isLockedMode();
  const isBusCompany = isBusCompanyUser();

  const selectedKey = location.pathname;

  // Filter menu items based on selected service and user role
  const filteredMenuItems = menuItems.filter((item) => {
    const service = item.meta?.service as string | undefined;
    const hideForBusCompany = item.meta?.hideForBusCompany as boolean | undefined;
    const busCompanyOnly = item.meta?.busCompanyOnly as boolean | undefined;

    // Service filter
    const serviceMatch = !service || service === 'all' || service === selectedService;

    // Role filter
    if (isBusCompany && hideForBusCompany) return false;
    if (!isBusCompany && busCompanyOnly) return false;

    return serviceMatch;
  });

  const menuItemsForAntd: MenuProps['items'] = filteredMenuItems.map((item) => {
    if (item.children && item.children.length > 0) {
      // Filter children based on service and role
      const filteredChildren = item.children.filter((child) => {
        const service = child.meta?.service as string | undefined;
        const hideForBusCompany = child.meta?.hideForBusCompany as boolean | undefined;
        const busCompanyOnly = child.meta?.busCompanyOnly as boolean | undefined;

        if (isBusCompany && hideForBusCompany) return false;
        if (!isBusCompany && busCompanyOnly) return false;

        return !service || service === 'all' || service === selectedService;
      });

      if (filteredChildren.length === 0) return null;

      return {
        key: item.key,
        icon: item.icon,
        label: item.label,
        children: filteredChildren.map((child) => ({
          key: child.key,
          icon: child.icon,
          label: child.label,
          onClick: () => navigate(child.route || '/'),
        })),
      };
    }
    return {
      key: item.key,
      icon: item.icon,
      label: item.label,
      onClick: () => navigate(item.route || '/'),
    };
  }).filter(Boolean);

  const handleServiceChange = (value: ServiceType) => {
    const service = services.find(s => s.key === value);
    if (service?.isAvailable) {
      setSelectedService(value);
      navigate('/');
      setDropdownOpen(false);
    }
  };

  const dropdownContent = (
    <div
      style={{
        background: '#1a1a2e',
        borderRadius: 12,
        padding: 8,
        minWidth: 260,
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div style={{ padding: '8px 12px', marginBottom: 4 }}>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
          Xizmat turini tanlang
        </span>
      </div>
      {services.map((service) => {
        const isSelected = selectedService === service.key;
        const isAvailable = service.isAvailable;

        return (
          <div
            key={service.key}
            onClick={() => isAvailable && handleServiceChange(service.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 12px',
              borderRadius: 8,
              cursor: isAvailable ? 'pointer' : 'not-allowed',
              background: isSelected ? 'rgba(52, 152, 219, 0.15)' : 'transparent',
              border: isSelected ? '1px solid rgba(52, 152, 219, 0.3)' : '1px solid transparent',
              marginBottom: 4,
              opacity: isAvailable ? 1 : 0.5,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (isAvailable && !isSelected) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: isAvailable
                  ? `linear-gradient(135deg, ${serviceColors[service.key]} 0%, ${serviceColors[service.key]}CC 100%)`
                  : 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                flexShrink: 0,
              }}
            >
              {serviceIcons[service.key] || (
                <span style={{ fontSize: 10, fontWeight: 600, color: '#F39C12' }}>TK</span>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  color: isAvailable ? '#fff' : 'rgba(255,255,255,0.4)',
                  fontWeight: 600,
                  fontSize: 14
                }}>
                  {service.label}
                </span>
                {!isAvailable && (
                  <Tag
                    style={{
                      background: 'rgba(243, 156, 18, 0.2)',
                      border: '1px solid rgba(243, 156, 18, 0.3)',
                      color: '#F39C12',
                      fontSize: 10,
                      padding: '0 6px',
                      lineHeight: '18px',
                      borderRadius: 4,
                      margin: 0,
                    }}
                  >
                    Tez kunda
                  </Tag>
                )}
              </div>
              <span style={{
                color: 'rgba(255,255,255,0.4)',
                fontSize: 12,
                display: 'block',
                marginTop: 2,
              }}>
                {service.description}
              </span>
            </div>
            {isSelected && (
              <CheckOutlined style={{ color: '#3498DB', fontSize: 16 }} />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={260}
      collapsedWidth={80}
      style={{
        overflow: 'hidden',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Logo Section */}
        <div
          style={{
            padding: collapsed ? '20px 16px' : '24px 20px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minHeight: 80,
          }}
        >
          <img
            src="/safargo.png"
            alt="Safaro"
            style={{
              width: collapsed ? 42 : 46,
              height: collapsed ? 42 : 46,
              borderRadius: 12,
              flexShrink: 0,
              objectFit: 'cover',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span
                style={{
                  color: '#FFFFFF',
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                }}
              >
                Safaro Admin
              </span>
              {lockedMode && (
                <span
                  style={{
                    color: taxiOnlyMode ? '#3498DB' : '#9B59B6',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    marginTop: 2,
                  }}
                >
                  {taxiOnlyMode ? 'TAXI & POCHTA' : 'AVTOBUSLAR'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Service Selector - hidden in locked mode or for bus company users */}
        {!lockedMode && !isBusCompany && (
        <div
          style={{
            padding: collapsed ? '12px 12px' : '12px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <Dropdown
            dropdownRender={() => dropdownContent}
            trigger={['click']}
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            placement="bottomLeft"
          >
            {collapsed ? (
              <Tooltip title={currentServiceInfo.label} placement="right">
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    background: `linear-gradient(135deg, ${serviceColors[selectedService]} 0%, ${serviceColors[selectedService]}CC 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    cursor: 'pointer',
                    margin: '0 auto',
                    boxShadow: `0 4px 15px ${serviceColors[selectedService]}40`,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {serviceIcons[selectedService]}
                </div>
              </Tooltip>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: `linear-gradient(135deg, ${serviceColors[selectedService]} 0%, ${serviceColors[selectedService]}CC 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    flexShrink: 0,
                    boxShadow: `0 4px 12px ${serviceColors[selectedService]}30`,
                  }}
                >
                  {serviceIcons[selectedService]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                    {currentServiceInfo.label}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                    Xizmat turi
                  </div>
                </div>
                <SwapOutlined style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }} />
              </div>
            )}
          </Dropdown>
        </div>
        )}

        {/* Main Menu */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 0' }}>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            defaultOpenKeys={collapsed ? [] : ['users']}
            items={menuItemsForAntd}
            style={{
              background: 'transparent',
              border: 'none',
            }}
          />
        </div>

        {/* Bottom Section */}
        <div
          style={{
            padding: collapsed ? '16px 12px' : '16px 16px',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          {/* Settings Button - hidden in bus-only mode and for bus company users */}
          {!busOnlyMode && !taxiOnlyMode && !isBusCompany && (
          <Tooltip title={collapsed ? 'Sozlamalar' : ''} placement="right">
            <Button
              type="text"
              icon={<SettingOutlined style={{ fontSize: 16 }} />}
              onClick={() => navigate('/settings')}
              style={{
                width: '100%',
                height: 46,
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 12,
                color: 'rgba(255, 255, 255, 0.65)',
                marginBottom: 8,
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 500,
                paddingLeft: collapsed ? 0 : 12,
              }}
            >
              {!collapsed && 'Sozlamalar'}
            </Button>
          </Tooltip>
          )}

          {/* Logout Button */}
          {(
          <Tooltip title={collapsed ? 'Chiqish' : ''} placement="right">
            <Button
              type="text"
              icon={<LogoutOutlined style={{ fontSize: 16 }} />}
              onClick={() => logout()}
              style={{
                width: '100%',
                height: 46,
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 12,
                color: '#ff6b6b',
                marginBottom: 16,
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 500,
                paddingLeft: collapsed ? 0 : 12,
              }}
            >
              {!collapsed && 'Chiqish'}
            </Button>
          </Tooltip>
          )}

          {/* Collapse Button */}
          <Button
            type="default"
            onClick={() => setCollapsed(!collapsed)}
            style={{
              width: '100%',
              height: 46,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              color: 'rgba(255, 255, 255, 0.85)',
              borderRadius: 10,
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {collapsed ? (
              <RightOutlined style={{ fontSize: 14 }} />
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <LeftOutlined style={{ fontSize: 12 }} />
                <span>Menyuni yopish</span>
              </span>
            )}
          </Button>
        </div>
      </div>
    </Sider>
  );
};
